import { Checkbox, Dialog, DialogContent, FormControlLabel, Grid, lighten, Typography, useTheme } from "@mui/material";
import React, { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { EmployeeRef } from "../../models/employees";
import { TnADateTime } from "../../models/tna";
import { TnADayDataDTO } from "../../models/tna/tna-data/tna-day-data-dto";
import { TnALocationCodeRef } from "../../models/tna-location-codes/tna-location-code-ref";
import { saveTnAClockings } from "../../services/tna-clockings/tna-clockings-api.service";
import { TNA_CLOCKING_TYPES } from "../../utils/constants/tna-constants";
import { TNA_TRACK_LOCATION_TYPES } from "../../utils/constants/track-location-constants";
import { fixHourFormat } from "../../utils/fix-hour-format";
import AddEditClockingLocation from "../AddEditClockingLocation/AddEditClockingLocation";
import { checkLocationMatching } from "../AddEditClockingLocation/utils";
import CustomConfirmationChangesDialog from "../CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import DialogActions from "../DialogActions/DialogActions";
import DialogTitle from "../DialogTitle/DialogTitle";
import { Buttons, Button } from "../MyClockings/styles";
import { diffInScheduleAndClocking } from "../MyClockings/utils";

interface Props {
  employee: EmployeeRef;
  forPrevDay?: boolean;
  clockingChanged?: (() => void) | undefined;
  tnATrackLocationId?: string;
  dayData?: TnADayDataDTO[];
  disabledButtons?: boolean;
}

interface State {
  clockingToSave: {
    date: Date;
    type: "clockIn" | "clockOut";
  } | null;
  nightShiftWarning: boolean;
  offScheduleWarning: boolean;
  hoursBeforeClosestInterval: number | null;
  tnALocationCodeOrName: string;
  tnALocationCode: TnALocationCodeRef | null;
  tnAlocationWarning: boolean;
  isLocationCCCDOpen: boolean;
  isLocationDialogOpen: boolean;
  loading: boolean;
}

const { ClockIn, ClockOut } = TNA_CLOCKING_TYPES;

const RealTimeClockingButtons = ({
  employee,
  forPrevDay,
  clockingChanged,
  tnATrackLocationId,
  dayData,
  disabledButtons,
}: Props) => {
  const tRP = "components.my_clockings";
  const theme = useTheme();
  const { t } = useTranslation();

  const [state, setState] = useState<State>({
    nightShiftWarning: false,
    offScheduleWarning: false,
    hoursBeforeClosestInterval: null,
    tnALocationCodeOrName: "",
    tnALocationCode: null,
    tnAlocationWarning: false,
    isLocationCCCDOpen: false,
    isLocationDialogOpen: false,
    loading: false,
    clockingToSave: null,
  });

  //#region save clocking
  const handleSaveTnAClocking = useCallback(
    async (tnALocationCode?: TnALocationCodeRef) => {
      // let matchingLocation: TnALocationCodeDTO | undefined = undefined;
      // if (
      //   tnAActualConfig?.tnAConfig?.tnATrackLocation?.id === TNA_TRACK_LOCATION_TYPES.ALLOW_ANY_WITH_FLAG &&
      //   !matchingLocation
      // ) {
      //   const { items } = await loadClockingLocationCodes({
      //     employeeId: employee.id,
      //     codeOrName: state.tnALocationCodeOrName.toLowerCase(),
      //   });
      //   matchingLocation = items[0] ?? undefined;

      //   if (!matchingLocation) {
      //     if (!state.locationWarning) {
      //       setState((prev) => ({
      //         ...prev,
      //         locationWarning: true,
      //       }));
      //       return;
      //     }
      //   }
      // }

      setState((state) => ({ ...state, loading: true, error: "" }));

      try {
        const { error } = await saveTnAClockings(
          employee,
          state.clockingToSave?.type === "clockIn" ? ClockIn.id : ClockOut.id,
          TnADateTime.FromDate(state.clockingToSave?.date ?? new Date()),
          tnALocationCode ?? state.tnALocationCode,
          state.tnALocationCodeOrName ? state.tnALocationCodeOrName : "",
          forPrevDay
        );

        if (error) {
          setState((state) => ({ ...state, error }));
        } else if (clockingChanged) {
          setState((state) => ({
            ...state,
            nightShiftWarning: false,
            clockingToSave: null,
            forPrevDay: false,
            offScheduleWarning: false,
            hoursBeforeClosestInterval: 0,
            tnALocationCodeOrName: "",
            tnALocationCode: null,
            tnAlocationWarning: false,
            isLocationDialogOpen: false,
          }));

          clockingChanged();
        } else {
          setState((state) => ({
            ...state,
            nightShiftWarning: false,
            clockingToSave: null,
            forPrevDay: false,
            offScheduleWarning: false,
            hoursBeforeClosestInterval: 0,
            tnALocationCodeOrName: "",
            tnALocationCode: null,
            tnAlocationWarning: false,
            isLocationDialogOpen: false,
          }));
        }
      } catch (error) {
        setState((state) => ({ ...state, error }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    },
    [clockingChanged, employee, state.clockingToSave, forPrevDay, state.tnALocationCode, state.tnALocationCodeOrName]
  );
  //#endregion

  //#region check location matching
  const checkClockingLocationMatch = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await checkLocationMatching(employee.id, state.tnALocationCodeOrName);
      if (response.error) {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      } else if (response.items[0]) {
        handleSaveTnAClocking(response.items[0] as TnALocationCodeRef);
      } else if (!response.items[0]) {
        setState((state) => ({
          ...state,
          tnAlocationWarning: true,
        }));
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [state.tnALocationCodeOrName, employee.id, handleSaveTnAClocking]);
  //#endregion

  //#region check track location
  const checkLocationTrackingType = useCallback(() => {
    if (!!tnATrackLocationId && tnATrackLocationId !== TNA_TRACK_LOCATION_TYPES.NOT_NEEDED) {
      setState((prev) => ({
        ...prev,
        isLocationDialogOpen: true,
        // clockingToSave: {
        //   date: date,
        //   type: clockingType,
        // },
        tnALocationCodeOrName: "",
        tnALocationCode: null,
      }));
      return;
    } else {
      handleSaveTnAClocking();
    }
  }, [tnATrackLocationId, handleSaveTnAClocking]);
  //#endregion

  //#region check schedule and nightShift before save
  const checkScheduleAndNightShift = useCallback(
    async (clockingToSave: { date: Date; type: "clockIn" | "clockOut" }) => {
      const year = clockingToSave.date.getFullYear();
      const month = clockingToSave.date.getMonth();
      const day = clockingToSave.date.getDate();

      const currDay = dayData?.find(
        (date) => date.tnADate?.year === year && date.tnADate?.month === month + 1 && date.tnADate?.day === day
      );

      if (!currDay?.prevDayNightShift) {
        const differenceInClockingsAndSchedule = diffInScheduleAndClocking(
          clockingToSave.date,
          dayData,
          forPrevDay ?? false
        );

        differenceInClockingsAndSchedule === null || differenceInClockingsAndSchedule > 1
          ? setState((state) => ({
              ...state,
              // clockingToSave: {
              //   date: date,
              //   type: clockingType,
              // },
              hoursBeforeClosestInterval: differenceInClockingsAndSchedule,
              offScheduleWarning: true,
              locationWarning: false,
              isLocationCCCDOpen: false,
            }))
          : await checkLocationTrackingType();
      } else {
        setState((state) => ({
          ...state,
          // clockingToSave: {
          //   date: date,
          //   type: clockingType,
          // },
          nightShiftWarning: true,
          locationWarning: false,
          isLocationCCCDOpen: false,
        }));
      }
    },
    [dayData, forPrevDay, checkLocationTrackingType, state.clockingToSave]
  );
  //#endregion

  //#region init clocking
  const initClocking = useCallback(
    (clockingType: "clockIn" | "clockOut") => {
      const clockingToSave = {
        type: clockingType,
        date: new Date(),
      };
      setState((state) => ({
        ...state,
        clockingToSave,
      }));

      checkScheduleAndNightShift(clockingToSave);
    },
    [checkScheduleAndNightShift]
  );
  //#endregion
  return (
    <>
      <Buttons>
        <Button
          variant="contained"
          onClick={() => initClocking("clockIn")}
          style={{
            backgroundColor: lighten(theme.palette.primary.main, 0.4),
          }}
          disabled={disabledButtons}
        >
          {t(`${tRP}.clock_in`)}
        </Button>
        <Button
          variant="contained"
          onClick={() => initClocking("clockOut")}
          style={{
            backgroundColor: lighten(theme.palette.warning.main, 0.4),
          }}
          disabled={disabledButtons}
        >
          {t(`${tRP}.clock_out`)}
        </Button>
      </Buttons>
      <CustomConfirmationChangesDialog
        open={state.nightShiftWarning}
        loading={state.loading}
        title={"Save clocking"}
        handleCancel={() => {
          setState((state) => ({
            ...state,
            nightShiftWarning: false,
            clockingToSave: null,
            forPrevDay: false,
            offScheduleWarning: false,
            hoursBeforeClosestInterval: 0,
          }));
        }}
        actionsAndHandlers={[
          {
            action: "Yes",
            buttonVariant: "contained",
            handler: () => {
              const differenceInClockingsAndSchedule = state.clockingToSave?.date
                ? diffInScheduleAndClocking(state.clockingToSave.date, dayData, forPrevDay ?? false)
                : null;

              differenceInClockingsAndSchedule === null || differenceInClockingsAndSchedule > 1
                ? setState((state) => ({
                    ...state,
                    offScheduleWarning: true,
                    hoursBeforeClosestInterval: differenceInClockingsAndSchedule,
                  }))
                : handleSaveTnAClocking();
            },
          },
        ]}
      >
        <Grid sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Typography>
            {!state.clockingToSave ? (
              "No clocking to save"
            ) : (
              <>
                Are you sure you want to save
                <b>{state.clockingToSave.type === "clockIn" ? " Clock in " : " Clock out "}</b> at{" "}
                <b>
                  {fixHourFormat(state.clockingToSave.date.getHours())} :{" "}
                  {fixHourFormat(state.clockingToSave?.date.getMinutes())}
                </b>
              </>
            )}
          </Typography>

          <FormControlLabel
            label={`${t("This clocking refers to the previous day")}`}
            control={
              <Checkbox
                value={forPrevDay}
                checked={forPrevDay}
                onChange={(e) =>
                  setState((state) => ({
                    ...state,
                    forPrevDay: e.target.checked,
                  }))
                }
              />
            }
          />
        </Grid>
      </CustomConfirmationChangesDialog>

      <CustomConfirmationChangesDialog
        open={state.offScheduleWarning}
        loading={state.loading}
        title={"Off expected schedule"}
        handleCancel={() => {
          setState((state) => ({
            ...state,
            nightShiftWarning: false,
            offScheduleWarning: false,
            clockingToSave: null,
            forPrevDay: false,
            hoursBeforeClosestInterval: 0,
          }));
        }}
        actionsAndHandlers={[
          {
            action: "Yes",
            buttonVariant: "contained",
            handler: () => {
              checkLocationTrackingType();
            },
          },
        ]}
      >
        <Grid sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Typography>
            {!state.clockingToSave ? (
              "No clocking to save"
            ) : (
              <>
                {state.hoursBeforeClosestInterval === null ? (
                  <>
                    We have noticed that you are reporting presence significantly off expected schedule (no schedule for
                    this day). Do you really want to save{" "}
                    <b>{state.clockingToSave.type === "clockIn" ? " Clock in " : " Clock out "}</b> at{" "}
                    <b>
                      {fixHourFormat(state.clockingToSave.date.getHours())} :{" "}
                      {fixHourFormat(state.clockingToSave.date.getMinutes())}
                    </b>{" "}
                    ?
                  </>
                ) : (
                  <>
                    <b>{state.clockingToSave.type === "clockIn" ? " Clock in " : " Clock out "}</b> at{" "}
                    <b>
                      {fixHourFormat(state.clockingToSave.date.getHours())} :{" "}
                      {fixHourFormat(state.clockingToSave.date.getMinutes())}
                    </b>{" "}
                    is
                    {" ("}
                    <b>{state.hoursBeforeClosestInterval.toFixed(2)}h</b> off schedule{")"}. Do you really want to
                    continue?
                  </>
                )}
              </>
            )}
          </Typography>
        </Grid>
      </CustomConfirmationChangesDialog>

      <Dialog
        open={state.isLocationDialogOpen}
        fullWidth
      >
        <DialogTitle
          title={t(`${tRP}.select_location`)}
          handleClose={() => setState((state) => ({ ...state, isLocationDialogOpen: false }))}
        />
        <DialogContent style={{ paddingTop: "15px" }}>
          <AddEditClockingLocation
            tnALocationCode={state.tnALocationCode}
            tnALocationCodeOrName={state.tnALocationCodeOrName}
            handleChangelocationInput={(tnALocationCodeOrName: string, tnALocationCode: TnALocationCodeRef | null) => {
              setState((prev) => ({
                ...prev,
                tnALocationCodeOrName,
                tnALocationCode,
                locationWarning: false,
              }));
            }}
            locationTrackingTypeId={tnATrackLocationId ?? ""}
            employeeId={employee.id}
            textFieldCustomStyle={{
              width: "300px",
            }}
            autoCompleteCustomStyle={{
              width: "300px",
            }}
            displayHint
          />
        </DialogContent>
        <DialogActions
          handleCancel={() => {
            setState((state) => ({ ...state, isLocationDialogOpen: false }));
          }}
          handleSave={() => {
            checkClockingLocationMatch();
          }}
          disabledSaveButton={
            ((tnATrackLocationId === TNA_TRACK_LOCATION_TYPES.ALLOW_ANY_LOCATION ||
              tnATrackLocationId === TNA_TRACK_LOCATION_TYPES.ALLOW_ANY_WITH_FLAG) &&
              !state.tnALocationCodeOrName) ||
            (tnATrackLocationId === TNA_TRACK_LOCATION_TYPES.ALLOW_ONLY_PREDEFINED && !state.tnALocationCode)
          }
        />
      </Dialog>

      <CustomConfirmationChangesDialog
        open={state.tnAlocationWarning}
        title={t(`${tRP}.location_confirmation`)}
        handleCancel={() => setState((state) => ({ ...state, tnAlocationWarning: false }))}
        loading={state.loading}
        actionsAndHandlers={[
          {
            action: "Yes",
            handler: () => handleSaveTnAClocking(),
            buttonVariant: "contained",
          },
        ]}
      >
        <Typography>
          <Trans
            i18nKey={`${tRP}.location_warning`}
            values={{ location: state.tnALocationCodeOrName }}
            components={{
              bold: <b />,
            }}
          />
        </Typography>
      </CustomConfirmationChangesDialog>
    </>
  );
};

export default RealTimeClockingButtons;
